import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import SublineApi from "../../services/SublineApi";
import PlantApi from "../../services/PlantApi";
import AreaApi from "../../services/AreaApi";
import LineApi from "../../services/LineApi";
import { useToast } from "../../context/ToastContext";

// Design System
import FormCard from "../../components/FormCard";
import Input from "../../components/Input/Input";
import ConfirmModal from "../../components/ConfirmModal/ConfirmModal";

const CreateSublineForm: React.FC = () => {
  const navigate = useNavigate();
  const { displayToast } = useToast();
  const { t: translateForm } = useTranslation("form");
  const { t: translateMessages } = useTranslation("messages");
  const { t: translateGeneral } = useTranslation("general");

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [plantId, setPlantId] = useState("");
  const [areaId, setAreaId] = useState("");
  const [lineId, setLineId] = useState("");
  
  const [plants, setPlants] = useState<{ label: string; value: string }[]>([]);
  const [areas, setAreas] = useState<{ label: string; value: string }[]>([]);
  const [lines, setLines] = useState<{ label: string; value: string }[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showConfirmCancel, setShowConfirmCancel] = useState(false);

  useEffect(() => {
    fetchPlants();
  }, []);

  useEffect(() => {
    if (plantId) {
      fetchAreas(plantId);
    } else {
      setAreas([]);
      setAreaId("");
      setLineId("");
    }
  }, [plantId]);

  useEffect(() => {
    if (areaId) {
      fetchLines(areaId);
    } else {
      setLines([]);
      setLineId("");
    }
  }, [areaId]);

  const fetchPlants = async (): Promise<void> => {
    try {
      const response = await PlantApi.GetAllPlants();
      const options = (response.data || []).map((p: any) => ({
        label: p.name,
        value: p.id,
      }));
      setPlants(options.sort((a: any, b: any) => a.label.localeCompare(b.label)));
    } catch (error) {
      console.error("Erro ao buscar plantas: ", error);
    }
  };

  const fetchAreas = async (pId: string): Promise<void> => {
    try {
      const response = await AreaApi.GetAreaByPlantIdStatic(pId);
      const options = (response.data || []).map((a: any) => ({
        label: a.name,
        value: a.id,
      }));
      setAreas(options.sort((a: any, b: any) => a.label.localeCompare(b.label)));
    } catch (error) {
      console.error("Erro ao buscar Ã¡reas: ", error);
    }
  };

  const fetchLines = async (aId: string): Promise<void> => {
    try {
      const response = await LineApi.GetLinesByArea(aId);
      const options = (response.data || []).map((l: any) => ({
        label: l.name,
        value: l.id,
      }));
      setLines(options.sort((a: any, b: any) => a.label.localeCompare(b.label)));
    } catch (error) {
      console.error("Erro ao buscar linhas: ", error);
    }
  };

  const handleSave = async (): Promise<void> => {
    if (!name || !lineId) {
      displayToast("error", translateForm("error"), translateMessages("fillAllFields"), false, 3000);
      return;
    }

    setIsLoading(true);
    try {
      // Duplicity check
      const sublinesResp = await SublineApi.GetAllSublines();
      const sublines = sublinesResp.data || [];
      const exists = sublines.some((s: any) => s.name.toLowerCase() === name.toLowerCase() && s.lineId === lineId);

      if (exists) {
        displayToast("error", translateMessages("duplicateSublineError"), "", false, 3000);
        return;
      }

      await SublineApi.CreateSubline(lineId, description, name);
      displayToast("success", translateMessages("registerSuccess"), "", false, 3000);
      navigate("/sublines");
    } catch (error: any) {
      displayToast("error", translateForm("error"), error.message || error, false, 3000);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <FormCard
      title={translateMessages("newSubline")}
      icon="add"
      onSubmit={handleSave}
      onCancel={() => setShowConfirmCancel(true)}
      submitButtonLabel={translateGeneral("save")}
      cancelButtonLabel={translateGeneral("cancel")}
      disabled={isLoading}
      numColumns={6}
    >
      <div style={{ gridColumn: "span 3" }}>
        <Input
          type="text"
          label={translateForm("name")}
          required
          value={name}
          onChange={(val: any) => setName(val)}
        />
      </div>
      <div style={{ gridColumn: "span 3" }}>
        <Input
          type="text"
          label={translateForm("description")}
          value={description}
          onChange={(val: any) => setDescription(val)}
        />
      </div>
      <div style={{ gridColumn: "span 2" }}>
        <Input
          type="select"
          label={translateForm("plant")}
          required
          value={plantId}
          options={plants}
          optionLabel="label"
          optionValue="value"
          onChange={(val: any) => {
            setPlantId(val);
            setAreaId("");
            setLineId("");
          }}
        />
      </div>
      <div style={{ gridColumn: "span 2" }}>
        <Input
          type="select"
          label={translateForm("area")}
          required
          disabled={!plantId}
          value={areaId}
          options={areas}
          optionLabel="label"
          optionValue="value"
          onChange={(val: any) => {
            setAreaId(val);
            setLineId("");
          }}
        />
      </div>
      <div style={{ gridColumn: "span 2" }}>
        <Input
          type="select"
          label={translateForm("line")}
          required
          disabled={!areaId}
          value={lineId}
          options={lines}
          optionLabel="label"
          optionValue="value"
          onChange={(val: any) => setLineId(val)}
        />
      </div>

      <ConfirmModal
        visible={showConfirmCancel}
        onHide={() => setShowConfirmCancel(false)}
        onConfirm={() => navigate("/sublines")}
      />
    </FormCard>
  );
};

export default CreateSublineForm;
